import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { 
  Droplet, Clock, ShieldCheck, CheckCircle, Calendar, MapPin, 
  ShoppingCart, ChevronDown, ChevronUp, FileText, 
  PhoneCall, MessageSquare, MessageCircle, Mail, Gift, Sparkles, Flame, Activity, 
  Award, UserCheck, Users, X, Info, AlertTriangle, Syringe,
  Star, Plus, Share2
} from 'lucide-react';
import api from '../../api/api';
import AuthGateModal from '../../components/AuthGateModal';
import ShareModal from '../../components/ShareModal/ShareModal';
import { getCurrentUserRole } from '../../utils/userRoleCheck';
import { resolveCatalogTest } from '../../utils/testResolver';
import RichTextRenderer from '../../components/ui/RichTextEditor/RichTextRenderer';
import './TestDetailContent.css';

export default function TestDetailContent({ test, isModal = false, onClose, allTests = [], onSelectTest, isInsurance = false }) {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, triggerAddToCartAnimation } = useCart();
  const userRole = getCurrentUserRole();
  const canViewInsurancePrices = userRole && (userRole.isStaff || ['super_admin', 'lab_owner', 'lab_manager', 'admin', 'billing', 'finance'].includes(userRole.role));
  const shouldMaskPrice = Boolean((isInsurance || test?.hide_price) && !canViewInsurancePrices);
  const [activeTab, setActiveTab] = useState('overview');
  const [added, setAdded] = useState(false);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [showAllRelated, setShowAllRelated] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);
  const [authGateModal, setAuthGateModal] = useState({ open: false, redirectTo: '/book' });
  const [noticeModal, setNoticeModal] = useState({ open: false, title: '', message: '', type: 'info' });
  const [showShareModal, setShowShareModal] = useState(false);
  const [fetchedTests, setFetchedTests] = useState([]);

  // Amazon-Style Reviews State
  const [reviewsData, setReviewsData] = useState({
    average_rating: 0,
    total_reviews: 0,
    star_counts: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
    star_percentages: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
    reviews: []
  });
  const [selectedStarFilter, setSelectedStarFilter] = useState('all');
  const [showWriteReviewModal, setShowWriteReviewModal] = useState(false);
  const [newReviewForm, setNewReviewForm] = useState({
    rating: 5,
    headline: '',
    comment: '',
    patient_name: '',
    email: ''
  });
  const [hoverStar, setHoverStar] = useState(0);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewMsg, setReviewMsg] = useState({ text: '', isError: false });

  const testId = test?.id || test?._id;

  const fetchReviews = React.useCallback(async () => {
    if (!testId) return;
    try {
      const res = await api.get(`/api/catalog/tests/${testId}/reviews/`);
      if (res.data) {
        setReviewsData(res.data);
      }
    } catch (err) {
      console.error("Failed to load test reviews", err);
    }
  }, [testId]);

  React.useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!newReviewForm.headline.trim() || !newReviewForm.comment.trim()) {
      setReviewMsg({ text: 'Please fill out all fields.', isError: true });
      return;
    }
    setSubmittingReview(true);
    setReviewMsg({ text: '', isError: false });
    try {
      const res = await api.post(`/api/catalog/tests/${testId}/reviews/submit/`, newReviewForm);
      if (res.status === 201 || res.status === 200) {
        setReviewMsg({ text: res.data.message || 'Review submitted successfully!', isError: false });
        setTimeout(() => {
          setShowWriteReviewModal(false);
          setNewReviewForm({ rating: 5, headline: '', comment: '', patient_name: '', email: '' });
          setReviewMsg({ text: '', isError: false });
          fetchReviews();
        }, 1200);
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to submit review.';
      setReviewMsg({ text: errMsg, isError: true });
      if (err.response?.status === 403) {
        setShowWriteReviewModal(false);
        setNoticeModal({
          open: true,
          title: 'Verified Purchase Required',
          message: errMsg,
          type: 'lock'
        });
      }
    } finally {
      setSubmittingReview(false);
    }
  };

  const cleanPatientEmail = (raw) => {
    if (!raw || typeof raw !== 'string') return '';
    const str = raw.trim();
    if (str.startsWith('ENC_') || str.startsWith('ENC_DEV_')) {
      return str;
    }
    if (!str.includes('@') || str.length < 5) {
      return '';
    }
    return str;
  };

  const handleOpenWriteReviewModal = () => {
    const userRole = getCurrentUserRole();

    // 1. If logged in as Staff/Partner (Admin, Phlebotomist, Hub Manager, Research, etc.)
    if (userRole && userRole.isStaff) {
      setNoticeModal({
        open: true,
        title: 'Patient Account Required',
        message: `Only Patients can post test reviews. You are currently logged in with a ${userRole.label} account (${userRole.email}). Please log in with a Patient account to submit a review.`,
        type: 'warning'
      });
      return;
    }

    // 2. Hydrate patient name & email
    let pName = '';
    let pEmail = '';

    try {
      const savedPatient = localStorage.getItem('patient_user');
      if (savedPatient) {
        const pObj = JSON.parse(savedPatient);
        if (pObj) {
          pEmail = cleanPatientEmail(pObj.email || pObj.patient_email || '');
          if (pObj.first_name || pObj.last_name) {
            pName = `${pObj.first_name || ''} ${pObj.last_name || ''}`.trim();
          } else if (pObj.patient_name) {
            pName = pObj.patient_name;
          } else if (pObj.name) {
            pName = pObj.name;
          }
        }
      }
    } catch (e) {}

    // Fallback: check userRole.email and direct storage keys
    if (!pEmail) {
      const keysToTry = [
        userRole?.email,
        localStorage.getItem('patient_email'),
        localStorage.getItem('user_email'),
        sessionStorage.getItem('patient_email')
      ];
      for (const k of keysToTry) {
        const cleaned = cleanPatientEmail(k);
        if (cleaned) {
          pEmail = cleaned;
          break;
        }
      }
    }

    const hasPatientToken = !!(localStorage.getItem('patient_token') || localStorage.getItem('token') || localStorage.getItem('patient_user'));

    // 3. If NOT logged in as patient at all:
    if (!pEmail && !hasPatientToken && (!userRole || !userRole.isPatient)) {
      setNoticeModal({
        open: true,
        title: 'Account Login Required',
        message: 'You must be logged in as a registered Patient to submit a review. Please sign in or create an account to continue.',
        type: 'lock'
      });
      return;
    }

    // 4. Hydrate & open review modal!
    const displayEmail = pEmail.startsWith('ENC_') ? '' : pEmail;
    const defaultDisplayName = pName || (displayEmail ? displayEmail.split('@')[0] : 'Verified Patient');

    setNewReviewForm({
      rating: 5,
      headline: '',
      comment: '',
      patient_name: defaultDisplayName,
      email: pEmail
    });
    setReviewMsg({ text: '', isError: false });
    setShowWriteReviewModal(true);
  };






  const filteredReviews = (reviewsData.reviews || []).filter(r => {
    if (selectedStarFilter === 'all') return true;
    return r.rating === selectedStarFilter;
  });

  React.useEffect(() => {
    if (allTests && allTests.length > 0) {
      setFetchedTests(allTests);
    } else {
      import('../../services/api').then(module => {
        if (module.superAdminAPI && module.superAdminAPI.getAdminTests) {
          module.superAdminAPI.getAdminTests().then(res => {
            const data = res.data?.results || res.data || [];
            if (Array.isArray(data)) setFetchedTests(data);
          }).catch(() => {});
        }
      }).catch(() => {});
    }
  }, [allTests]);

  const wrapperRef = React.useRef(null);
  const isManualClickRef = React.useRef(false);

  const inCart = (cartItems || []).some(item => String(item.id || item._id) === String(testId));


  // Accordion state
  const [openAccordions, setOpenAccordions] = useState({
    included: true,
    interpretation: false,
    important: false,
    disclaimer: false
  });

  // Ensure body scroll is locked while modal is open
  React.useEffect(() => {
    if (isModal) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      };
    }
  }, [isModal]);

  // Ensure modal container scrolls to top when opened
  React.useEffect(() => {
    const containers = document.querySelectorAll('.test-modal-card-container');
    containers.forEach(c => { if (c) c.scrollTop = 0; });
  }, [test]);

  // Scrollspy effect: highlight current section tab dynamically as user scrolls
  React.useEffect(() => {
    const sectionIds = ['overview', 'preparation', 'collection', 'included', 'results', 'faq'];

    const getScrollContainer = () => {
      if (!isModal) return window;
      if (wrapperRef.current) {
        const parent = wrapperRef.current.closest('.test-modal-card-container') || wrapperRef.current.closest('.test-modal-container');
        if (parent) return parent;
      }
      return document.querySelector('.test-modal-card-container') || document.querySelector('.test-modal-container') || window;
    };

    let activeContainer = null;

    const handleScroll = () => {
      if (isManualClickRef.current) return;

      const container = getScrollContainer();
      if (!container) return;

      const containerTop = (isModal && container !== window) ? container.getBoundingClientRect().top : 0;

      let currentSection = 'overview';
      for (const id of sectionIds) {
        const el = document.getElementById(`sec-${id}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          const topOffset = (isModal && container !== window) ? (rect.top - containerTop) : rect.top;
          if (topOffset <= 150) {
            currentSection = id;
          }
        }
      }
      setActiveTab(currentSection);
    };

    const attachListener = () => {
      const target = getScrollContainer();
      if (target) {
        if (activeContainer && activeContainer !== target) {
          activeContainer.removeEventListener('scroll', handleScroll);
        }
        activeContainer = target;
        target.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
      }
    };

    attachListener();
    const timer = setTimeout(attachListener, 100);

    return () => {
      clearTimeout(timer);
      if (activeContainer) {
        activeContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isModal, test]);

  // Auto-scroll active tab button smoothly into view on mobile/tablet horizontal scroll bar
  React.useEffect(() => {
    if (!activeTab) return;
    const activeBtn = document.querySelector(`.nav-tab-btn[data-tab="${activeTab}"]`);
    if (activeBtn) {
      activeBtn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeTab]);

  if (!test) return null;

  const toggleAccordion = (key) => {
    setOpenAccordions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAddToCart = (e) => {
    if (inCart) {
      removeFromCart(testId);
    } else {
      triggerAddToCartAnimation(e, {
        id: testId,
        title: test.title,
        price: discPrice,
        original_price: origPrice,
        category: test.category_names ? (Array.isArray(test.category_names) ? test.category_names[0] : test.category_names) : 'General Wellness',
        turnaround: turnaround,
        sample_matrix: sampleType,
        collection_methods: collectionMethod,
        fasting_required: test.fasting_required,
        doctor_order_required: test.doctor_order_required
      });
      setAdded(true);
      setTimeout(() => setAdded(false), 1500);
    }
  };

  const handleBookNow = () => {
    const userRole = getCurrentUserRole();
    if (userRole.isStaff) {
      setAuthGateModal({ open: true, redirectTo: '/book' });
      return;
    }
    handleAddToCart();
    if (onClose) onClose();
    navigate('/book');
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    isManualClickRef.current = true;
    setTimeout(() => {
      isManualClickRef.current = false;
    }, 800);

    const targetEl = document.getElementById(`sec-${tabId}`);
    if (!targetEl) return;

    if (isModal) {
      const container = targetEl.closest('.test-modal-card-container') || targetEl.closest('.test-modal-container');
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = targetEl.getBoundingClientRect();
        const targetTop = targetRect.top - containerRect.top + container.scrollTop - 65;
        container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
        return;
      }
    }

    const yOffset = -90;
    const y = targetEl.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  // Dynamic field extraction from MongoDB test response object
  const origPrice = parseFloat(test.price || test.market_price || 0);
  const discPrice = parseFloat(test.discount_price || test.discounted_price || test.price || 0);
  const code = test.test_code || test.code || test.sku || 'MUSB-TEST';
  const sku = test.sku || test.test_code || 'MUSB-SKU-001';

  // Taxonomy & Specimen matrix attributes
  const sampleType = Array.isArray(test.sample_matrix)
    ? test.sample_matrix.join(', ')
    : (test.sample_matrix || test.sample_type || test.specimen_type || 'Whole Blood (EDTA)');

  // Collection methods parsing & line-by-line formatting
  const rawCollectionMethods = Array.isArray(test.collection_methods)
    ? test.collection_methods.filter(Boolean)
    : (test.collection_methods || test.collection_method
        ? String(test.collection_methods || test.collection_method).split(',').map(s => s.trim()).filter(Boolean)
        : []);

  const collectionMethod = rawCollectionMethods.join(', ');
  
  // Concise short summary for sidebar and hero highlight row (guarantees single-line fit)
  const collectionMethodShort = rawCollectionMethods.length > 1 
    ? 'Home & Center' 
    : (rawCollectionMethods[0] || 'Standard Draw');

  const rawTurnaroundVal = Array.isArray(test.turnaround_time)
    ? test.turnaround_time.join(', ')
    : Array.isArray(test.turnaround)
    ? test.turnaround.join(', ')
    : (test.turnaround_time || test.turnaround || '1-2 Business days');
  const rawTurnaroundStr = String(rawTurnaroundVal || '1-2 Business days').replace(/&nbsp;/gi, ' ');
  const turnaroundOptions = rawTurnaroundStr.includes(',')
    ? rawTurnaroundStr.split(',').map(s => s.trim()).filter(Boolean)
    : [rawTurnaroundStr];
  
  const turnaround = turnaroundOptions.length > 1
    ? `${turnaroundOptions[0]} (${turnaroundOptions.length} Options)`
    : (turnaroundOptions[0] || '1-2 Business days');
    
  const turnaroundShort = String(turnaround).includes('Same Day') ? 'Same Day / 1-2 Days' : turnaround;

  const fastingText = typeof test.fasting_required === 'boolean'
    ? (test.fasting_required ? 'Required (8-12 Hrs)' : 'No Fasting Required')
    : (test.fasting_required || 'No Fasting Required');

  // Clean age group formatting for sidebar (avoid "No minimum age+ Yrs")
  const rawAge = String(test.target_age_group || test.age_group || '');
  const ageGroupShort = rawAge
    ? rawAge.replace(/No minimum age\+?\s*(Yrs)?/gi, 'All Ages').replace(/All Ages\+?\s*Yrs/gi, 'All Ages').trim()
    : (test.min_age ? `${test.min_age}+ Yrs` : 'All Ages');

  const homeCollection = (test.home_collection || test.home_collection_available || test.mobile_phlebotomy_available || test.home_collection_eligible) ? 'Available' : 'In-Clinic / Center';
  const clinicalReview = (test.clinical_review || test.physician_review || test.clinical_review_required) ? 'Included' : 'Standard Validation';

  // Descriptions & Instructions (Clean raw &nbsp; entities)
  const shortDesc = (test.short_description || test.description || test.full_description || '').replace(/&nbsp;/gi, ' ');
  const fullDesc = (test.full_description || test.description || test.short_description || '').replace(/&nbsp;/gi, ' ');
  const prepInstructions = (test.prep_instructions || test.preparation_instructions || test.patient_preparation || test.preparation || '').replace(/&nbsp;/gi, ' ');

  // Array Fields (Included Biomarkers & FAQs)
  const biomarkersList = Array.isArray(test.included_biomarkers) && test.included_biomarkers.length > 0
    ? test.included_biomarkers
    : (Array.isArray(test.biomarkers) && test.biomarkers.length > 0
        ? test.biomarkers
        : (Array.isArray(test.includes) && test.includes.length > 0
            ? test.includes
            : []));

  const faqsList = Array.isArray(test.faqs) && test.faqs.length > 0
    ? test.faqs
    : (Array.isArray(test.faq) && test.faq.length > 0 ? test.faq : []);

  // Category tags & badges deduplication
  const rawCategories = Array.isArray(test.category_names)
    ? test.category_names
    : (test.category_names ? [test.category_names] : (test.category ? [test.category] : ['General Wellness']));

  const rawUsageTags = Array.isArray(test.usage_tags)
    ? test.usage_tags
    : (Array.isArray(test.badges) ? test.badges : []);

  // Deduplicate and filter out redundant tags that repeat info
  const categoriesList = Array.from(new Set(rawCategories.filter(Boolean)));
  const usageTagsList = Array.from(
    new Set(
      rawUsageTags
        .filter(Boolean)
        .filter(t => !categoriesList.includes(t))
        .filter(t => !['No Fasting Required', 'Same-Day Results', 'Standard'].includes(t))
    )
  );

  const usageTag = usageTagsList.length > 0
    ? usageTagsList[0]
    : (test.usage_tag && String(test.usage_tag).toLowerCase() !== 'standard' ? test.usage_tag : '');

  // Combined Tags Pill List for Hero Card
  const allTagsCombined = [
    ...categoriesList.map(c => ({ type: 'category', label: c })),
    ...usageTagsList.map(t => ({ type: 'tag', label: t }))
  ];
  const visibleTagsList = showAllTags ? allTagsCombined : allTagsCombined.slice(0, 4);
  const hiddenTagsCount = allTagsCombined.length - 4;

  // Dynamic Related Tests Filtering & Matching
  const currentId = String(testId || test?.id || test?._id || '');
  const currentCategories = (categoriesList || []).map(c => String(c).toLowerCase());

  const availableCatalog = fetchedTests.length > 0 ? fetchedTests : (allTests || []);
  const explicitRelatedKeys = Array.isArray(test?.related_tests) ? test.related_tests : (test?.related_tests ? [test.related_tests] : []);
  const explicitRelatedDetails = Array.isArray(test?.related_test_details) ? test.related_test_details : [];

  const explicitMatched = [];
  
  if (explicitRelatedDetails.length > 0) {
    explicitRelatedDetails.forEach(d => {
      if (d && String(d.id || d._id) !== currentId) {
        explicitMatched.push(d);
      }
    });
  }

  explicitRelatedKeys.forEach(relKey => {
    if (!relKey) return;
    const keyStr = String(relKey).trim().toLowerCase();
    const found = availableCatalog.find(t => {
      const tId = String(t.id || t._id || '').toLowerCase();
      const tTitle = String(t.title || t.name || '').toLowerCase();
      const tDisplayName = String(t.display_name || '').toLowerCase();
      const tCode = String(t.test_code || t.code || t.sku || '').toLowerCase();
      return tId !== currentId && (tId === keyStr || tTitle === keyStr || tDisplayName === keyStr || tCode === keyStr || tTitle.includes(keyStr));
    });
    if (found && !explicitMatched.some(m => String(m.id || m._id) === String(found.id || found._id))) {
      explicitMatched.push(found);
    }
  });

  let displayRelated = [];
  const hasExplicitMatches = explicitMatched.length > 0;

  // 1. If Super Admin explicitly selected related tests for this item, render ALL those selected tests!
  if (hasExplicitMatches) {
    displayRelated = explicitMatched;
  } else if (currentCategories.length > 0 && currentCategories[0] !== 'general wellness') {
    // 2. Otherwise, fall back ONLY to matching items in the EXACT SAME medical category
    displayRelated = availableCatalog.filter(t => {
      const tId = String(t.id || t._id || '');
      if (tId === currentId) return false;

      const tCats = Array.isArray(t.category_names)
        ? t.category_names.map(c => String(c).toLowerCase())
        : [String(t.category_names || t.category || '').toLowerCase()];

      return tCats.some(c => currentCategories.includes(c));
    });
  }


  return (
    <div ref={wrapperRef} className={`test-detail-page-wrapper ${isModal ? 'is-modal' : ''}`}>
      {/* Sticky Header Bar with Title and Close Button for Modal Mode */}
      {isModal && onClose && (
        <div className="modal-sticky-header">
          <div className="modal-sticky-header-title">
            <Droplet size={18} color="#2563eb" />
            <span>{test?.title || test?.name || 'Test Details'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              className="modal-sticky-share-btn"
              onClick={() => setShowShareModal(true)}
              title="Share this test via link, WhatsApp, SMS, or social media"
            >
              <Share2 size={15} />
              <span>Share</span>
            </button>
            <button type="button" className="modal-sticky-close-btn" onClick={onClose} title="Close Modal">
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Top Breadcrumb Header (Only if full page) */}
      {!isModal && (
        <div className="test-breadcrumb-bar">
          <span>Home</span> &rsaquo; <span>Lab Tests</span> &rsaquo; <span>{test.category_names ? (Array.isArray(test.category_names) ? test.category_names[0] : test.category_names) : 'General Wellness'}</span> &rsaquo; <span className="current">{test.title}</span>
        </div>
      )}

      {/* Hero Header Card */}
      <div className="test-hero-card">
        <div className="hero-center-info">
          {usageTag && (
            <div className="hero-top-badge-row">
              <span className="badge-purple-pill">
                <Sparkles size={12} /> {usageTag.toUpperCase()}
              </span>
            </div>
          )}

          <h1 className="hero-test-title">{test.title}</h1>

          <div className="hero-code-sku-row">
            <span>Code: <strong>{code}</strong></span>
            <span className="sep">&bull;</span>
            <span>SKU: <strong>{sku}</strong></span>
          </div>

          {/* Hero Amazon-Style Rating Stars Badge */}
          <div className="hero-rating-stars-badge" onClick={() => handleTabClick('reviews')} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', margin: '4px 0 10px 0' }}>
            <div style={{ display: 'flex', color: '#f59e0b', gap: '2px' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={15}
                  fill={reviewsData.total_reviews > 0 && star <= Math.round(reviewsData.average_rating) ? '#f59e0b' : 'none'}
                  color="#f59e0b"
                  className="star-ic-anim"
                />
              ))}
            </div>
            <span style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a' }}>
              {reviewsData.total_reviews > 0 ? reviewsData.average_rating : '0.0'}
            </span>
            <span style={{ fontSize: '0.82rem', color: '#2563eb', fontWeight: 600 }}>
              ({reviewsData.total_reviews} patient rating{reviewsData.total_reviews === 1 ? '' : 's'})
            </span>
          </div>



          <div
            className="hero-short-desc"
            dangerouslySetInnerHTML={{ __html: shortDesc }}
          />

          {/* Prominent Fasting, Doctor Order, and Clinical Review Alerts */}
          {test.fasting_required && (
            <div className="hero-alert-banner warning">
              <AlertTriangle size={18} color="#d97706" style={{ flexShrink: 0 }} />
              <span><strong>Fasting Required:</strong> Do not eat or drink anything except water for {test.fasting_duration || '8-12 hours'} prior to your sample collection.</span>
            </div>
          )}
          {test.doctor_order_required && (
            <div className="hero-alert-banner info">
              <FileText size={18} color="#2563eb" style={{ flexShrink: 0 }} />
              <span><strong>Doctor's Order Required:</strong> A physician's order is mandatory for this test. You must upload your order document before completing booking.</span>
            </div>
          )}
          {(test.requires_clinical_review || test.clinical_review_required) && (
            <div className="hero-alert-banner success">
              <ShieldCheck size={18} color="#16a34a" style={{ flexShrink: 0 }} />
              <div style={{ minWidth: 0, maxWidth: '100%', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                <span><strong>Physician &amp; Clinical Review Included:</strong> Every result is reviewed by a board-certified clinical laboratory professional.</span>
                {test.clinical_review_instructions && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.82rem', color: '#166534', fontWeight: 500, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{test.clinical_review_instructions}</p>
                )}
              </div>
            </div>
          )}

          {/* 4 Feature Icons Row (2x2 Grid on Mobile) */}
          <div className="hero-icons-grid">
            <div className="icon-item">
              <ShieldCheck size={16} className="ic-blue" />
              <span>{fastingText}</span>
            </div>
            <div className="icon-item">
              <Clock size={16} className="ic-blue" />
              <span>{turnaroundShort}</span>
            </div>
            <div className="icon-item">
              <Droplet size={16} className="ic-blue" />
              <span>Specimen: {sampleType}</span>
            </div>
            <div className="icon-item">
              <MapPin size={16} className="ic-blue" />
              <span>{homeCollection === 'Available' ? 'Home & Center Collection' : 'Center Draw Only'}</span>
            </div>
          </div>

          {/* Badges pills row with +N More Tags Expand Toggle */}
          <div className="hero-badges-tags-list">
            <span className="lbl">Categories &amp; Medical Tags:</span>
            {visibleTagsList.map((item, i) => (
              <span key={`t-${i}`} className={`pill ${item.type === 'category' ? 'category-pill' : 'tag-pill'}`}>
                {item.type === 'category' ? <CheckCircle size={11} /> : <Flame size={11} />} {item.label}
              </span>
            ))}
            {allTagsCombined.length > 4 && (
              <button
                type="button"
                className="pill tag-toggle-pill"
                onClick={() => setShowAllTags(!showAllTags)}
                style={{
                  background: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  color: '#0284c7',
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  padding: '0.25rem 0.65rem',
                  borderRadius: '14px',
                  cursor: 'pointer'
                }}
              >
                {showAllTags ? 'Show Less ▲' : `+${hiddenTagsCount} More Tags ▼`}
              </button>
            )}
            {allTagsCombined.length === 0 && (
              <>
                <span className="pill category-pill"><ShieldCheck size={11} /> CLIA Certified</span>
                <span className="pill tag-pill"><Award size={11} /> Physician Reviewed</span>
              </>
            )}
          </div>
        </div>

        {/* Hero Right Action Card */}
        <div className="hero-right-action-card">
          <div className="price-header-row">
            {shouldMaskPrice ? (
              <span className="special-pill" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)', padding: '6px 14px', borderRadius: '8px', fontSize: '0.95rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <ShieldCheck size={18} color="#10b981" /> Billed to Insurance
              </span>
            ) : (
              <>
                {origPrice > discPrice && (
                  <span className="reg-price">Regular Price: <del>${origPrice.toFixed(2)}</del></span>
                )}
                <span className="our-lbl">Our Price</span>
                <span className="main-price">${discPrice.toFixed(2)}</span>
                <span className="special-pill"><CheckCircle size={12} color="#15803d" /> Online Special</span>
              </>
            )}
          </div>

          <div className="action-buttons-wrap">
            <button 
              type="button" 
              className={`btn-add-cart-primary ${inCart || added ? 'added' : ''}`}
              onClick={handleAddToCart}
              title={inCart ? "Click to remove from cart" : "Add to cart"}
            >
              <ShoppingCart size={16} /> {inCart || added ? 'Added to Cart ✓' : 'Add to Cart'}
            </button>
            <button 
              type="button" 
              className="btn-book-now-secondary"
              onClick={handleBookNow}
            >
              <Calendar size={16} /> Book Now
            </button>
            <button 
              type="button" 
              className="btn-share-hero-pill"
              onClick={() => setShowShareModal(true)}
              title="Share this test via link, WhatsApp, SMS, or social media"
            >
              <Share2 size={15} /> Share Test
            </button>
          </div>
        </div>
      </div>

      {/* RELATED TESTS & FREQUENTLY ORDERED TOGETHER — 4 cards shown initially with View More expand toggle */}
      {displayRelated.length > 0 && (
        <div id="sec-related" className="sec-related-box">
          <div className="sec-related-header-wrap">
            <h3 className="section-title sec-related-title">
              <Sparkles size={18} className="ic-purple" /> Related Tests &amp; Frequently Ordered Together
            </h3>
            <span className="sec-related-subtext">
              {hasExplicitMatches ? 'Curated diagnostic recommendations' : 'Matched by medical category & specimen type'}
            </span>
          </div>
          <p className="sec-related-desc">
            Patients who ordered <strong>{test.title}</strong> also frequently included these diagnostic panels:
          </p>
          <div className="related-tests-grid">
            {(showAllRelated ? displayRelated : displayRelated.slice(0, 4)).map((relTest, rIdx) => {
              const rPrice = relTest.discount_price || relTest.price || 0;
              const rCategory = Array.isArray(relTest.category_names) ? relTest.category_names[0] : (relTest.category_names || relTest.category || 'General Wellness');
              const rawTurnaroundVal = Array.isArray(relTest.turnaround_time)
                ? (relTest.turnaround_time[0] || '')
                : Array.isArray(relTest.turnaround)
                ? (relTest.turnaround[0] || '')
                : (relTest.turnaround_time || relTest.turnaround || '1-2 Days');
              let rawTurnaround = String(rawTurnaroundVal || '1-2 Days');
              if (rawTurnaround.includes(',')) {
                rawTurnaround = rawTurnaround.split(',')[0].trim();
              }
              let rTurnaround = rawTurnaround.replace(/Business\s*days?/gi, 'Days').replace(/days?\s*days?/gi, 'Days').trim();
              if (rTurnaround.toLowerCase().includes('same day')) {
                rTurnaround = 'Same Day / 1 Day';
              } else if (!rTurnaround.toLowerCase().includes('day')) {
                rTurnaround = `${rTurnaround} Days`;
              }
              const rInCart = (cartItems || []).some(item => String(item.id || item._id) === String(relTest.id || relTest._id));
              const rawRelDesc = relTest.short_description || relTest.description || 'Comprehensive diagnostic panel for wellness.';
              const rCleanDesc = String(rawRelDesc).replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
              return (
                <div key={rIdx} className="related-test-card">
                  <div className="rel-card-top">
                    <span className="rel-cat-pill">{rCategory}</span>
                    <span className="rel-price">{shouldMaskPrice ? 'Billed to Insurance' : `$${Number(rPrice).toFixed(2)}`}</span>
                  </div>
                  <h4 className="rel-test-title">{relTest.title || relTest.name}</h4>
                  <p className="rel-test-desc">
                    {rCleanDesc}
                  </p>
                  <div className="rel-card-footer">
                    <span className="rel-turnaround"><Clock size={12} /> {rTurnaround}</span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        className="btn-view-rel-test"
                        onClick={() => onSelectTest ? onSelectTest(relTest) : navigate(`/test/${relTest.id || relTest._id}`)}
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className={`btn-add-rel-cart ${rInCart ? 'added' : ''}`}
                        onClick={(e) => rInCart ? removeFromCart(relTest.id || relTest._id) : triggerAddToCartAnimation(e, relTest)}
                      >
                        {rInCart ? 'Added ✓' : '+ Add'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {displayRelated.length > 4 && (
            <div style={{ textAlign: 'center', marginTop: '1.1rem' }}>
              <button
                type="button"
                className="btn-show-more-related"
                onClick={() => setShowAllRelated(!showAllRelated)}
                style={{
                  background: '#ffffff',
                  border: '1.5px solid #0284c7',
                  color: '#0284c7',
                  fontWeight: 700,
                  fontSize: '0.84rem',
                  padding: '0.55rem 1.35rem',
                  borderRadius: '24px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 3px 10px rgba(2, 132, 199, 0.12)',
                  transition: 'all 0.2s ease'
                }}
              >
                {showAllRelated ? (
                  <>Show Less <ChevronUp size={16} /></>
                ) : (
                  <>View More Related Tests ({displayRelated.length - 4} More) <ChevronDown size={16} /></>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Sticky Tab Navigation Bar */}
      <div className="test-nav-tabs-bar">
        <div className="nav-tabs-scroll-list">
          {/* Overview always shown */}
          <button data-tab="overview" className={`nav-tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => handleTabClick('overview')}>
            <FileText size={14} className="tab-ic-anim" /> Overview
          </button>
          {/* Preparation — only when prep data exists */}
          {(prepInstructions || test.fasting_required) && (
            <button data-tab="preparation" className={`nav-tab-btn ${activeTab === 'preparation' ? 'active' : ''}`} onClick={() => handleTabClick('preparation')}>
              <ShieldCheck size={14} className="tab-ic-anim" /> Preparation
            </button>
          )}
          {/* Collection Info — only when collection methods exist */}
          {rawCollectionMethods.length > 0 && (
            <button data-tab="collection" className={`nav-tab-btn ${activeTab === 'collection' ? 'active' : ''}`} onClick={() => handleTabClick('collection')}>
              <Syringe size={14} className="tab-ic-anim" /> Collection Info
            </button>
          )}
          {/* What's Included — only when biomarkers exist */}
          {biomarkersList.length > 0 && (
            <button data-tab="included" className={`nav-tab-btn ${activeTab === 'included' ? 'active' : ''}`} onClick={() => handleTabClick('included')}>
              <Activity size={14} className="tab-ic-anim" /> What's Included
            </button>
          )}
          {/* Results — only when results/clinical data exists */}
          {(test.clinical_significance || test.clinical_review_instructions || test.result_interpretation || test.turnaround_time || test.turnaround) && (
            <button data-tab="results" className={`nav-tab-btn ${activeTab === 'results' ? 'active' : ''}`} onClick={() => handleTabClick('results')}>
              <Clock size={14} className="tab-ic-anim" /> Results
            </button>
          )}
          {/* FAQ — only when faqs exist */}
          {faqsList.length > 0 && (
            <button data-tab="faq" className={`nav-tab-btn ${activeTab === 'faq' ? 'active' : ''}`} onClick={() => handleTabClick('faq')}>
              <Info size={14} className="tab-ic-anim" /> FAQ
            </button>
          )}
          {/* Patient Reviews */}
          <button data-tab="reviews" className={`nav-tab-btn ${activeTab === 'reviews' ? 'active' : ''}`} onClick={() => handleTabClick('reviews')}>
            <Star size={14} className="tab-ic-anim" /> Patient Reviews ({reviewsData.total_reviews})
          </button>
        </div>
      </div>


      {/* Main Two-Column Layout */}
      <div className="test-main-content-grid">
        {/* Left Column (All Sections Rendered Sequentially) */}
        <div className="left-content-column">
          

          {/* SECTION 1: OVERVIEW */}
          <div id="sec-overview" className="content-section-box">
            <h3 className="section-title"><Activity size={18} className="ic-blue" /> About this test</h3>
            <RichTextRenderer content={shortDesc} className="about-text-lead" />
            {fullDesc && fullDesc !== shortDesc && (
              <>
                {showFullDesc && (
                  <RichTextRenderer content={fullDesc} className="about-text-sub fade-in" style={{ marginTop: '0.75rem', lineHeight: 1.6 }} />
                )}
                <button 
                  type="button" 
                  className="btn-toggle-desc"
                  onClick={() => setShowFullDesc(!showFullDesc)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#1d4ed8',
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    padding: 0,
                    margin: '0.4rem 0 1rem 0',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {showFullDesc ? 'Show Less ▲' : 'Read Full Overview ▼'}
                </button>
              </>
            )}

            {/* 4 Cards Grid - Derived strictly from real MongoDB / Catalog item fields */}
            <div className="four-highlight-cards-grid">
              {sampleType && (
                <div className="hl-card">
                  <div className="hl-icon red"><Droplet size={15} className="hl-ic-anim" /></div>
                  <h4>Specimen Type</h4>
                  <p>{sampleType}</p>
                </div>
              )}
              {collectionMethod && (
                <div className="hl-card">
                  <div className="hl-icon blue"><Syringe size={15} className="hl-ic-anim" /></div>
                  <h4>Collection Method</h4>
                  <p>{collectionMethod}</p>
                </div>
              )}
              {turnaround && (
                <div className="hl-card">
                  <div className="hl-icon pink"><Clock size={15} className="hl-ic-anim" /></div>
                  <h4>Turnaround Time</h4>
                  <p>{turnaround}</p>
                </div>
              )}
              <div className="hl-card">
                <div className="hl-icon purple"><ShieldCheck size={15} className="hl-ic-anim" /></div>
                <h4>Fasting &amp; Prep</h4>
                <p>{fastingText}</p>
              </div>
            </div>
          </div>


          {/* SECTION 2: PREPARATION (Rendered ONLY if preparation instructions exist or fasting is required) */}
          {(prepInstructions || test.fasting_required) && (
            <div id="sec-preparation" className="content-section-box">
              <h3 className="section-title"><ShieldCheck size={18} className="ic-green" /> Patient Preparation Instructions</h3>
              <div className="prep-hero-callout">
                <h4>What you need to do before your test</h4>
                <RichTextRenderer content={prepInstructions || (test.fasting_required ? 'Fasting is required. Do not eat or drink anything except water for 8-12 hours before your appointment.' : '')} />
              </div>
            </div>
          )}

          {/* SECTION 3: COLLECTION INFO (patient-facing: collection methods only) */}
          {rawCollectionMethods.length > 0 && (
            <div id="sec-collection" className="content-section-box">
              <div className="collection-hdr-flex">
                <h3 className="section-title" style={{ margin: 0 }}>
                  <Droplet size={18} className="ic-red" /> Specimen Collection & Logistics
                </h3>
              </div>

              <div className="collection-methods-wrapper">
                <div className="detail-card-box">
                  <h4 className="card-box-header"><MapPin size={16} className="ic-blue" /> Collection Options</h4>
                  <div className="collection-options-list">
                    {rawCollectionMethods.map((m, idx) => (
                      <div key={typeof m === 'string' ? `${m}-${idx}` : idx} className="collection-option-row">
                        <CheckCircle size={16} className="ic-green" style={{ flexShrink: 0 }} />
                        <span>{m}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 4: WHAT'S INCLUDED (Rendered ONLY if biomarkers exist) */}
          {biomarkersList.length > 0 && (
            <div id="sec-included" className="content-section-box">
              <h3 className="section-title"><FileText size={18} className="ic-blue" /> Included Biomarkers &amp; Components</h3>
              <p className="bm-count-subtext">
                This panel includes {biomarkersList.length} measurement{biomarkersList.length > 1 ? 's' : ''}:
              </p>
              <div className="biomarkers-flat-list">
                {biomarkersList
                  .map((b, idx) => {
                    const resolved = resolveCatalogTest(b, availableCatalog);
                    if (!resolved) return null;
                    const title = resolved.title || resolved.name || `Biomarker ${idx + 1}`;
                    const subtext = resolved.short_description || (typeof b === 'object' && b.unit ? `Unit: ${b.unit}` : (typeof b === 'object' && b.description ? b.description : ''));
                    const isClickable = onSelectTest && resolved.id && !String(resolved.id).startsWith('inc-');

                    return (
                      <div
                        key={resolved.id || resolved.code || `${title}-${idx}`}
                        className={`bm-list-row ${isClickable ? 'is-clickable' : ''}`}
                        onClick={() => isClickable && onSelectTest(resolved)}
                        style={{ cursor: isClickable ? 'pointer' : 'default' }}
                      >
                        <div className="bm-row-dot" />
                        <div className="bm-row-body">
                          <span className="bm-row-title">{title}</span>
                          {subtext && <span className="bm-row-sub">{subtext}</span>}
                        </div>
                      </div>
                    );
                  })
                  .filter(Boolean)}
              </div>
            </div>
          )}

          {/* SECTION 5: RESULTS (Rendered ONLY if clinical instructions, significance or turnaround exist) */}
          {(test.clinical_significance || test.clinical_review_instructions || test.result_interpretation || turnaround) && (
            <div id="sec-results" className="content-section-box">
              <h3 className="section-title"><Activity size={18} className="ic-blue" /> Results &amp; Clinical Interpretation</h3>
              <div className="results-tab-container">
                {turnaround && (
                  <div className="res-block">
                    <h4>Turnaround Time</h4>
                    <p><strong>{turnaround}</strong></p>
                  </div>
                )}
                {test.clinical_review_instructions && (
                  <div className="res-block">
                    <h4>Clinical Review Notes</h4>
                    <RichTextRenderer content={test.clinical_review_instructions} />
                  </div>
                )}
                {(test.clinical_significance || test.result_interpretation) && (
                  <div className="res-block amber">
                    <h4>Clinical Significance</h4>
                    <RichTextRenderer content={test.clinical_significance || test.result_interpretation} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SECTION 6: FAQ (Rendered ONLY if FAQs exist) */}
          {faqsList.length > 0 && (
            <div id="sec-faq" className="content-section-box">
              <h3 className="section-title"><Info size={18} className="ic-blue" /> Frequently Asked Questions</h3>
              <div className="faq-list">
                {faqsList.map((f, idx) => (
                  <div key={f.question || f.q || idx} className="faq-item">
                    <div className="faq-q">Q: {f.question || f.q}</div>
                    <div className="faq-a">{f.answer || f.a}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Related Tests moved to top of overview — see SECTION 0 above */}

          {/* SECTION 7: AMAZON-STYLE CUSTOMER REVIEWS & REAL PATIENT RATINGS */}
          <div id="sec-reviews" className="content-section-box" style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #e2e8f0' }}>
            <h3 className="section-title" style={{ margin: '0 0 1rem 0' }}>
              <MessageSquare size={18} className="ic-blue" /> Patient Reviews &amp; Ratings
            </h3>

            <div className="reviews-amazon-grid">
              {/* Left Rating Summary Column */}
              <div className="reviews-summary-column">
                <div className="overall-score-box">
                  <div className="score-inline-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span className="score-num-inline" style={{ fontSize: '1.5rem', fontWeight: 900, color: '#0f172a' }}>
                      {reviewsData.total_reviews > 0 ? reviewsData.average_rating : '0.0'}
                    </span>
                    <div className="score-stars-row" style={{ display: 'flex', gap: '3px', color: '#f59e0b' }}>
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} size={18} fill={reviewsData.total_reviews > 0 && s <= Math.round(reviewsData.average_rating) ? '#f59e0b' : 'none'} color="#f59e0b" className="star-ic-anim" />
                      ))}
                    </div>
                  </div>
                  <div className="score-subtext">
                    {reviewsData.total_reviews > 0
                      ? `${reviewsData.average_rating} out of 5 (${reviewsData.total_reviews} global rating${reviewsData.total_reviews === 1 ? '' : 's'})`
                      : '0.0 out of 5 (0 global ratings)'}
                  </div>
                </div>


                {/* 5-Star Distribution Breakdown Progress Bars */}
                <div className="star-breakdown-list">
                  {[5, 4, 3, 2, 1].map((starVal) => {
                    const pct = reviewsData.star_percentages?.[starVal] || 0;
                    return (
                      <div
                        key={starVal}
                        className={`star-bar-row ${selectedStarFilter === starVal ? 'active-filter' : ''}`}
                        onClick={() => setSelectedStarFilter(selectedStarFilter === starVal ? 'all' : starVal)}
                        title={`Filter by ${starVal} star reviews`}
                      >
                        <span className="star-lbl">{starVal} star</span>
                        <div className="bar-track">
                          <div className="bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="star-pct">{pct}%</span>
                      </div>
                    );
                  })}
                </div>

                <div className="write-review-cta-box">
                  <h4>Review this diagnostic test</h4>
                  <p>Share your experience with other patients and medical cohorts.</p>
                  <button
                    type="button"
                    className="btn-write-review"
                    onClick={handleOpenWriteReviewModal}
                  >
                    <Plus size={16} /> Write a Patient Review
                  </button>
                </div>
              </div>

              {/* Right Reviews List Column */}
              <div className="reviews-list-column">
                <div className="reviews-filter-header">
                  <span className="hdr-title">
                    {selectedStarFilter === 'all' ? 'Top Patient Reviews' : `Showing ${selectedStarFilter}-Star Reviews`}
                  </span>
                  {selectedStarFilter !== 'all' && (
                    <button type="button" className="btn-clear-filter" onClick={() => setSelectedStarFilter('all')}>
                      Clear Filter (Show All)
                    </button>
                  )}
                </div>

                {filteredReviews.length === 0 ? (
                  <div className="no-reviews-empty">
                    <MessageSquare size={32} color="#94a3b8" />
                    <p>No patient reviews recorded for this star rating yet.</p>
                    <button type="button" className="btn-write-review-sm" onClick={handleOpenWriteReviewModal}>
                      Be the first to review this test
                    </button>
                  </div>
                ) : (
                  <div className="reviews-cards-stream">
                    {filteredReviews.map((rev, rIdx) => (
                      <div key={rev.id || rIdx} className="amazon-review-card">
                        <div className="rev-header">
                          <div className="rev-avatar">
                            {(rev.patient_name || 'V').charAt(0).toUpperCase()}
                          </div>
                          <div className="rev-user-info">
                            <span className="rev-name">{rev.patient_name || 'Verified Patient'}</span>
                            {rev.verified_order && (
                              <span className="verified-badge">
                                <CheckCircle size={12} color="#16a34a" /> Verified Patient Purchase
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="rev-stars-row">
                          <div className="stars">
                            {[1, 2, 3, 4, 5].map((st) => (
                              <Star key={st} size={14} fill={st <= rev.rating ? '#f59e0b' : 'none'} color="#f59e0b" />
                            ))}
                          </div>
                          <span className="rev-headline">{rev.headline}</span>
                        </div>

                        <div className="rev-date">
                          Reviewed on {rev.created_at ? new Date(rev.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Recently'}
                        </div>

                        <p className="rev-comment">{rev.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Collapsible Accordion Sections (Always rendered at bottom for quick reference) */}
          <div className="accordions-container-wrap">


            {/* Result Interpretation Accordion */}
            {(test.clinical_significance || test.result_interpretation) && (
              <div className="accordion-item-card">
                <button className="accordion-header-btn" onClick={() => toggleAccordion('interpretation')}>
                  <span className="title-wrap"><Activity size={16} className="ic-blue" /> Result Interpretation</span>
                  {openAccordions.interpretation ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {openAccordions.interpretation && (
                  <div className="accordion-body-content">
                    <RichTextRenderer content={test.clinical_significance || test.result_interpretation} style={{ fontSize: '0.85rem', color: '#334155', lineHeight: 1.6 }} />
                  </div>
                )}
              </div>
            )}

            {/* Important Information Accordion */}
            {(test.important_information || test.patient_instructions || test.state_restrictions) && (
              <div className="accordion-item-card">
                <button className="accordion-header-btn" onClick={() => toggleAccordion('important')}>
                  <span className="title-wrap"><Info size={16} className="ic-blue" /> Important Information</span>
                  {openAccordions.important ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {openAccordions.important && (
                  <div className="accordion-body-content">
                    <RichTextRenderer content={test.important_information || test.patient_instructions || (test.state_restrictions ? `State Restrictions: ${test.state_restrictions}` : '')} style={{ fontSize: '0.85rem', color: '#334155', lineHeight: 1.6 }} />
                  </div>
                )}
              </div>
            )}

            {/* Medical Disclaimer Accordion */}
            {test.medical_disclaimer && (
              <div className="accordion-item-card">
                <button className="accordion-header-btn" onClick={() => toggleAccordion('disclaimer')}>
                  <span className="title-wrap"><AlertTriangle size={16} className="ic-amber" /> Medical Disclaimer</span>
                  {openAccordions.disclaimer ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {openAccordions.disclaimer && (
                  <div className="accordion-body-content">
                    <RichTextRenderer content={test.medical_disclaimer} style={{ fontSize: '0.85rem', color: '#334155', lineHeight: 1.6 }} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column (Sidebar) */}
        <div className="right-sidebar-column">
          {/* Test Summary Card */}
          <div className="sidebar-card-box">
            <h4 className="sidebar-card-title">Test Summary</h4>
            <div className="sidebar-summary-list">
              <div className="sum-item">
                <span className="sum-lbl"><Droplet size={14} className="ic-red" /> Sample</span>
                <span className="sum-val">{sampleType}</span>
              </div>
              <div className="sum-item">
                <span className="sum-lbl"><Syringe size={14} className="ic-blue" /> Collection</span>
                <span className="sum-val">{collectionMethodShort}</span>
              </div>
              <div className="sum-item">
                <span className="sum-lbl"><ShieldCheck size={14} className="ic-amber" /> Fasting</span>
                <span className="sum-val">{fastingText}</span>
              </div>
              <div className="sum-item">
                <span className="sum-lbl"><Clock size={14} className="ic-blue" /> Turnaround Time</span>
                <span className="sum-val">{turnaroundShort}</span>
              </div>
              <div className="sum-item">
                <span className="sum-lbl"><Users size={14} className="ic-purple" /> Age</span>
                <span className="sum-val">{ageGroupShort}</span>
              </div>
              <div className="sum-item">
                <span className="sum-lbl"><MapPin size={14} className="ic-green" /> Home Collection</span>
                <span className="sum-val">{homeCollection}</span>
              </div>
              <div className="sum-item">
                <span className="sum-lbl"><UserCheck size={14} className="ic-teal" /> Clinical Review</span>
                <span className="sum-val">{clinicalReview}</span>
              </div>
              <div className="sum-item price-row">
                <span className="sum-lbl">Price</span>
                <span className="sum-price" style={shouldMaskPrice ? { color: '#10b981', fontSize: '0.95rem', fontWeight: 800 } : undefined}>
                  {shouldMaskPrice ? 'Billed to Insurance' : `$${discPrice.toFixed(2)}`}
                </span>
              </div>
            </div>
          </div>

          {/* Why choose MusB Labs? */}
          <div className="sidebar-card-box">
            <h4 className="sidebar-card-title">Why choose MusB Labs?</h4>
            <ul className="why-us-checklist">
              <li><CheckCircle size={14} className="ic-green" /> CLIA-Certified & COLA-Accredited Labs</li>
              <li><CheckCircle size={14} className="ic-green" /> Fast & reliable results</li>
              <li><CheckCircle size={14} className="ic-green" /> Secure & confidential</li>
              <li><CheckCircle size={14} className="ic-green" /> Easy online booking</li>
              <li><CheckCircle size={14} className="ic-green" /> Home & center collection</li>
              <li><CheckCircle size={14} className="ic-green" /> Trusted by physicians</li>
            </ul>
          </div>

          {/* Save More with Health Packages Banner */}
          <div className="sidebar-promo-banner-card">
            <div className="banner-left">
              <h4>Save More with Health Packages</h4>
              <p>Combine this test with other popular tests and save up to 40%.</p>
              <button 
                type="button" 
                className="btn-view-packages" 
                onClick={() => {
                  if (onClose) onClose();
                  navigate('/offers');
                }}
              >
                View Packages &rsaquo;
              </button>
            </div>
            <div className="banner-icon">
              <Gift size={36} color="#ffffff" />
            </div>
          </div>

          {/* Need Help? Box (Matches Screenshots 2 & 3 with 100% Clickable Channels) */}
          <div className="sidebar-card-box">
            <h4 className="sidebar-card-title">Need Help?</h4>
            <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.75rem' }}>
              Talk to the <strong>MusB Diagnostics</strong> care team for help with preparation, collection, or results.
            </p>
            <div className="contact-info-rows" style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              <a href="tel:+18134190781" className="contact-link" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#0f172a', fontWeight: 700, textDecoration: 'none' }}>
                <PhoneCall size={15} className="ic-blue" /> +1 (813) 419-0781
              </a>
              <a href="https://wa.me/17275050452" target="_blank" rel="noopener noreferrer" className="contact-link" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#00e676', fontWeight: 700, textDecoration: 'none' }}>
                <MessageCircle size={15} style={{ color: '#00e676' }} /> WhatsApp +1 (727) 505-0452
              </a>
              <a href="mailto:info@musbdiagnostics.com" className="contact-link" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#2563eb', fontWeight: 600, textDecoration: 'none', fontSize: '0.82rem' }}>
                <Mail size={15} className="ic-blue" /> info@musbdiagnostics.com
              </a>
              <div 
                className="contact-link blue" 
                onClick={() => { if (onClose) onClose(); navigate('/contact'); }}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#0284c7', fontWeight: 700, marginTop: '0.2rem' }}
                title="Go to Contact Us page"
              >
                <MessageSquare size={15} /> Contact Us &rsaquo;
              </div>
              <span className="hours-text" style={{ fontSize: '0.73rem', color: '#94a3b8', marginTop: '0.2rem' }}>Mon&ndash;Fri 7AM&ndash;6PM | Sat 8AM&ndash;1PM</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Assurance Banner */}
      <div className="test-bottom-assurance-banner">
        <div className="assurance-left">
          <ShieldCheck size={28} className="ic-blue" />
          <div>
            <h4>Your Health. Our Priority.</h4>
            <p>We follow strict quality standards and use advanced technology to ensure accurate results you can trust.</p>
          </div>
        </div>
      </div>

      {/* Footer Disclaimer */}
      <div className="test-bottom-disclaimer-text">
        This test is not intended to diagnose or treat any disease. Always consult your healthcare provider.
      </div>
      <AuthGateModal 
        open={authGateModal.open}
        onClose={() => setAuthGateModal({ open: false, redirectTo: '/book' })}
        redirectTo={authGateModal.redirectTo}
      />

      {/* Write Patient Review Modal */}
      {showWriteReviewModal && (
        <div className="review-modal-backdrop">
          <div className="review-modal-card">
            <div className="review-modal-header">
              <h3>Write a Patient Review</h3>
              <button type="button" onClick={() => setShowWriteReviewModal(false)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleReviewSubmit}>
              <div className="form-group">
                <label>Overall Rating</label>
                <div className="star-picker-row">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      size={28}
                      className="picker-star"
                      fill={(hoverStar || newReviewForm.rating) >= s ? '#f59e0b' : 'none'}
                      color={(hoverStar || newReviewForm.rating) >= s ? '#f59e0b' : '#cbd5e1'}
                      onMouseEnter={() => setHoverStar(s)}
                      onMouseLeave={() => setHoverStar(0)}
                      onClick={() => setNewReviewForm({ ...newReviewForm, rating: s })}
                      style={{ cursor: 'pointer', transition: 'transform 0.15s' }}
                    />
                  ))}
                  <span className="star-lbl-text">
                    {(hoverStar || newReviewForm.rating) === 5 && '5.0 - Excellent'}
                    {(hoverStar || newReviewForm.rating) === 4 && '4.0 - Good'}
                    {(hoverStar || newReviewForm.rating) === 3 && '3.0 - Average'}
                    {(hoverStar || newReviewForm.rating) === 2 && '2.0 - Fair'}
                    {(hoverStar || newReviewForm.rating) === 1 && '1.0 - Poor'}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label>Headline / Summary</label>
                <input
                  type="text"
                  placeholder="e.g. Fast turnaround, painless home draw"
                  value={newReviewForm.headline}
                  onChange={(e) => setNewReviewForm({ ...newReviewForm, headline: e.target.value })}
                  required
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label>Your Review Comments</label>
                <textarea
                  rows="4"
                  placeholder="Share details about your sample collection, staff professionalism, turnaround speed..."
                  value={newReviewForm.comment}
                  onChange={(e) => setNewReviewForm({ ...newReviewForm, comment: e.target.value })}
                  required
                  maxLength={2000}
                />
              </div>

              <div className="form-group-row">
                <div>
                  <label>Your Name (e.g. Sarah M.)</label>
                  <input
                    type="text"
                    placeholder="e.g. Sarah Miller"
                    value={newReviewForm.patient_name}
                    onChange={(e) => setNewReviewForm({ ...newReviewForm, patient_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label>Patient Email</label>
                  <input
                    type="email"
                    placeholder="e.g. patient@example.com"
                    value={newReviewForm.email}
                    onChange={(e) => setNewReviewForm({ ...newReviewForm, email: e.target.value })}
                    required
                  />
                </div>

              </div>
              <p style={{ fontSize: '0.78rem', color: '#64748b', margin: '-0.5rem 0 1rem 0', fontStyle: 'italic' }}>
                🔒 Privacy Guarantee: Your email is used exclusively for verified purchase checks and will <strong>never</strong> be displayed on public review cards.
              </p>


              {reviewMsg.text && (
                <div className={`review-msg-box ${reviewMsg.isError ? 'error' : 'success'}`}>
                  {reviewMsg.text}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowWriteReviewModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submittingReview}>
                  {submittingReview ? 'Publishing Review...' : 'Submit Review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modern Custom Notification Modal */}
      {noticeModal.open && (

        <div className="review-modal-backdrop" style={{ zIndex: 10000 }}>
          <div className="review-modal-card" style={{ maxWidth: '440px', textAlign: 'center', padding: '2rem 1.75rem' }}>
            <div style={{
              width: '54px', height: '54px', borderRadius: '50%',
              background: noticeModal.type === 'warning' ? '#fef3c7' : noticeModal.type === 'lock' ? '#eff6ff' : '#f0fdf4',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem auto'
            }}>
              {noticeModal.type === 'warning' && <AlertTriangle size={28} color="#d97706" />}
              {noticeModal.type === 'lock' && <ShieldCheck size={28} color="#2563eb" />}
              {noticeModal.type === 'info' && <Info size={28} color="#16a34a" />}
            </div>

            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: '0 0 0.5rem 0' }}>
              {noticeModal.title}
            </h3>
            
            <p style={{ fontSize: '0.88rem', color: '#475569', lineHeight: 1.55, margin: '0 0 1.5rem 0' }}>
              {noticeModal.message}
            </p>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              {noticeModal.type === 'lock' ? (
                <>
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setNoticeModal({ ...noticeModal, open: false })}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-submit"
                    onClick={() => {
                      setNoticeModal({ ...noticeModal, open: false });
                      setAuthGateModal({ open: true, redirectTo: `/test/${testId || ''}` });
                    }}
                  >
                    Log In / Sign Up
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn-submit"
                  style={{ width: '100%' }}
                  onClick={() => setNoticeModal({ ...noticeModal, open: false })}
                >
                  Understood
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title="Share Diagnostic Test"
        testName={test?.title || test?.name || 'Diagnostic Test'}
        testId={test?.id || test?._id || test?.code}
        testSlug={test?.slug || (test?.title ? test.title.toLowerCase().replace(/[^a-z0-9]+/g, '-') : '')}
        category={test?.category_names ? (Array.isArray(test.category_names) ? test.category_names[0] : test.category_names) : (test?.category_name || test?.category)}
      />
    </div>
  );
}


